import {darken, lighten, ThemeOptions} from '@mui/material/styles';

export const FALLBACK_UNAUTH_COLOR = {
  primary: '#1F98D9',
  secondary: 'rgba(0,0,0,0.6)',
};

const breakpoints = {
  unit: 'em',
  values: {
    xs: 0,
    sm: 30,
    md: 48,
    lg: 78,
    xl: 120,
  },
};

export const baseMembersThemeOptions: ThemeOptions = {
  breakpoints,
  typography: {
    fontFamily: ['Source Sans Pro', 'sans-serif'].join(','),
    fontSize: 14,
  },
  palette: {
    primary: {
      main: '#34C5EE',
      contrastText: '#fff',
    },
    secondary: {
      main: '#DCDCDC',
      contrastText: '#fff',
    },
    error: {
      main: '#FF3638',
    },
    warning: {
      main: '#FACC0F',
    },
    action: {
      hoverOpacity: 0.08,
      focusOpacity: 0.24,
    },
  },
  components: {
    MuiFormControl: {
      defaultProps: {
        variant: 'outlined',
        margin: 'dense',
        fullWidth: true,
        size: 'small',
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&:not(.Mui-disabled)': {
            color: 'text.primary',
          },
          '& fieldset': {
            borderRadius: `2px`,
          },
          '&.Mui-disabled fieldset': {
            opacity: 0.5,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '100px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '100px',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
  },
};

const themePalette = {
  blue: '#006fa6',
  orange: '#d03e00',
  green: '#00790d',
  red: '#b80014',
  purple: '#af3290',
  grey: '#494949',
};

/**
 * @see apps/sites.patientrewardshub.com/application/assets/stylesheets/private/themes/template.colors.scss
 */
const themeColorMapping: Record<number, keyof typeof themePalette> = {
  1: 'blue',
  2: 'orange',
  3: 'orange',
  4: 'blue',
  5: 'red',
  6: 'green',
  7: 'red',
  8: 'red',
  9: 'blue',
  10: 'green',
  11: 'purple',
  12: 'green',
  13: 'grey',
  14: 'orange',
};

export const getColorsForMemberThemeId = (themeId: number) => ({
  primary: themePalette[themeColorMapping[themeId]],
  secondary: FALLBACK_UNAUTH_COLOR.secondary,
});

export const getExpandedPaletteForColor = (color: string) => ({
  main: color,
  dark: darken(color, 0.2),
  light: lighten(color, 0.2),
});

export type ThemeOption = {
  id: number;
  color: string;
  name: string;
};

/**
 * Gets all available theme options with their colors and names
 */
export const getAvailableThemes = (): ThemeOption[] => {
  const themes: ThemeOption[] = [];

  Object.entries(themeColorMapping).forEach(([id, colorKey]) => {
    const themeId = parseInt(id, 10);
    const color = themePalette[colorKey];
    const name = colorKey;

    themes.push({
      id: themeId,
      color,
      name,
    });
  });

  return themes;
};
